/**
 * VEXOS Saved Quotes Dashboard.
 *
 * Landing page for the VEXOS Client Calculator section. Lists every quote
 * the rep has saved, with stats, filters, sort, and CRUD actions. Clicking
 * "+ New VEXOS Quote" or any card's Edit action opens the calculator at
 * /sales/client-calculator/new or /sales/client-calculator/:id respectively.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, useAuth } from "../../App";
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit3,
  Copy,
  Trash2,
  CheckCircle2,
  FileText,
  Eye,
  ChevronDown,
  AlertTriangle,
  FileSignature,
  Receipt,
  CreditCard,
  Copy as CopyIcon,
  List as ListIcon,
  Columns3,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import StrategyCallModal from "../../components/calculators/StrategyCallModal";
import CustomInvoiceModal, { InvoiceStatusPill } from "../../components/calculators/CustomInvoiceModal";
import { quoteContractTotal } from "../../components/intake/quoteMapping";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "saved", label: "Saved" },
  { id: "proposal-sent", label: "Sent" },
];
const TYPE_FILTERS = [
  { id: "all-types", label: "All types" },
  { id: "condo", label: "Condo" },
  { id: "community", label: "Community" },
  { id: "custom_invoice", label: "Invoices" },
];
const SORT_OPTIONS = [
  { id: "recent", label: "Most recent" },
  { id: "oldest", label: "Oldest" },
  { id: "value-desc", label: "Highest value" },
  { id: "value-asc", label: "Lowest value" },
  { id: "name-asc", label: "A–Z" },
  { id: "rep-asc", label: "Sales rep" },
];
const ALL_REPS = "all-reps";

// Board view — cold → warm → hot, left to right. Keys are fixed (they are what
// a quote stores); titles are team-editable and come from the backend.
const BOARD_COLUMNS = [
  { id: "cold", accent: "#7FA7C9" },
  { id: "warm", accent: "#D9A44C" },
  { id: "hot",  accent: "#D46A4C" },
];
const DEFAULT_BOARD_TITLES = { cold: "Cold", warm: "Warm", hot: "Hot" };
// A quote never placed on the board sits in the first column.
const temperatureOf = (q) =>
  BOARD_COLUMNS.some((c) => c.id === q?.lead_temperature) ? q.lead_temperature : "cold";
const VIEW_STORAGE_KEY = "vexos-quotes-view";
const readStoredView = () => {
  try { return localStorage.getItem(VIEW_STORAGE_KEY) === "board" ? "board" : "list"; } catch { return "list"; }
};
const UNASSIGNED_REP = "Unassigned";

// Mirrors REP_OPTIONS in VexosClientCalculator.jsx. An internal account can
// attribute a proposal to a client-facing rep, and that attribution — not the
// HQ login that happened to save the quote — is whose quote it is.
const REP_LABELS = {
  "julian ruiz": "Julian Ruiz",
  "jordan evans": "Jordan Evans",
  "bernardo essenfeld": "Bernardo Essenfeld",
  "cole becker": "Cole Becker",
};

const fmtUSD = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const titleCase = (s) =>
  String(s || "").split(" ").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

// The rep a quote belongs to: the proposal rep chosen in the calculator if one
// was picked, otherwise whoever created it. Never empty, so every quote lands
// in exactly one bucket when sorting or filtering by rep.
const repName = (q) => {
  const key = String(q?.inputs?.salesRep || "").trim().toLowerCase();
  if (key) return REP_LABELS[key] || titleCase(key);
  // Stored creator names are inconsistently cased ("julian ruiz" vs
  // "Cole Becker"); fold known reps to one spelling so a rep is one bucket.
  const creator = String(q?.created_by_name || q?.created_by || "").trim();
  return REP_LABELS[creator.toLowerCase()] || creator || UNASSIGNED_REP;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

// Custom invoices (Custom Invoice button) share this pool; their money is the
// invoice total, and they carry no calc_snapshot.
const isInvoice = (q) => q?.quote_kind === "custom_invoice";

// A quote's contract value: one-time scope + the AGREED platform monthly × term
// + collateral — the figure the proposal and the signed agreement state. Not
// calc_snapshot.grandTotal: quotes saved before 2026-10-27 stored that with the
// rate-card monthly, ignoring the rep's override and the CRM deduction
// (Shopped at Wilton Manors: $77,860 here against $65,860 on the agreement).
const tcv = (q) => (isInvoice(q) ? Number(q?.invoice?.total || 0) : quoteContractTotal(q));

const bundleLabel = (q) => {
  const b = q?.calc_snapshot?.bundle;
  if (!b || !b.label || (b.pct || 0) === 0) return null;
  return b.label;
};

const StatusPill = ({ status }) => {
  const map = {
    draft:           { label: "Draft", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.55)", border: "rgba(255,255,255,0.1)" },
    saved:           { label: "Saved", bg: "rgba(143,167,155,0.12)", color: "#8FA79B", border: "rgba(143,167,155,0.35)" },
    "proposal-sent": { label: "Sent",  bg: "rgba(10,138,74,0.14)",   color: "#3ECF83", border: "rgba(60,201,122,0.35)" },
  };
  const s = map[status] || map.saved;
  return (
    <span
      style={{
        fontSize: "10px",
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        padding: "3px 9px",
        borderRadius: "999px",
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
      }}
      data-testid={`status-pill-${status}`}
    >
      {s.label}
    </span>
  );
};

export default function VexosSavedQuotes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Quotes are a shared team pool — tag which ones the current rep created
  // and gate destructive delete to owner + admin (mirrors the backend).
  const currentUserId = user?.id || user?.email || null;
  const isAdmin = ["admin", "coo", "ceo", "founder", "super_admin"].includes(
    (user?.role || "").toLowerCase()
  );
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all-types");
  const [sortKey, setSortKey] = useState("recent");
  const [sortOpen, setSortOpen] = useState(false);
  const [repFilter, setRepFilter] = useState(ALL_REPS);
  const [repOpen, setRepOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [strategyCallOpen, setStrategyCallOpen] = useState(false);
  // { invoice: null } = create a new one; { invoice: quote } = open that one.
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(readStoredView);
  const [boardTitles, setBoardTitles] = useState(DEFAULT_BOARD_TITLES);

  const changeView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { /* per-viewer nicety only */ }
  };

  useEffect(() => {
    apiClient.get("/vexos-quotes/board-columns")
      .then((res) => { if (res.data?.titles) setBoardTitles(res.data.titles); })
      .catch((e) => console.error(e));
  }, []);

  const renameColumn = async (id, title) => {
    const prev = boardTitles;
    const next = { ...prev, [id]: title.trim() || DEFAULT_BOARD_TITLES[id] };
    if (next[id] === prev[id]) return;
    setBoardTitles(next);
    try {
      const res = await apiClient.put("/vexos-quotes/board-columns", { titles: { [id]: title } });
      setBoardTitles(res.data.titles);
    } catch (e) {
      setBoardTitles(prev);
      toast.error("Failed to rename column");
      console.error(e);
    }
  };

  // Optimistic: the card moves now and snaps back if the save fails.
  const moveQuote = async (id, temperature) => {
    const before = quotes.find((q) => q.id === id);
    if (!before || temperatureOf(before) === temperature) return;
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, lead_temperature: temperature } : q)));
    try {
      await apiClient.patch(`/vexos-quotes/${id}`, { lead_temperature: temperature });
    } catch (e) {
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, lead_temperature: before.lead_temperature } : q)));
      toast.error("Failed to move quote");
      console.error(e);
    }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/vexos-quotes");
      setQuotes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to load saved quotes");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // ?invoice=<id> — the deep link on the "Invoice paid" notification.
  const invoiceParam = searchParams.get("invoice");
  useEffect(() => {
    if (!invoiceParam || loading) return;
    const q = quotes.find((x) => x.id === invoiceParam && isInvoice(x));
    if (q) setInvoiceModal({ invoice: q });
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete("invoice"); return n; }, { replace: true });
  }, [invoiceParam, loading, quotes, setSearchParams]);

  // Created or refreshed in the popup → put it on the list without a reload.
  const upsertQuote = useCallback((q) => {
    if (!q?.id) return;
    setQuotes((qs) => (qs.some((x) => x.id === q.id) ? qs.map((x) => (x.id === q.id ? q : x)) : [q, ...qs]));
  }, []);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Payment link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  // Close hover menus when clicking outside
  useEffect(() => {
    const onClick = () => { setMenuOpenId(null); setSortOpen(false); setRepOpen(false); };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, []);

  const stats = useMemo(() => {
    const totalQuotes = quotes.length;
    // Pipeline = proposals. An invoice bills work that is usually already on
    // some project's quote, so counting it here would count that money twice.
    const pipelineValue = quotes.filter((q) => !isInvoice(q)).reduce((s, q) => s + tcv(q), 0);
    const proposalsSent = quotes.filter((q) => q.status === "proposal-sent").length;
    return { totalQuotes, pipelineValue, proposalsSent };
  }, [quotes]);

  // Every rep who owns at least one quote, with a count, so the filter only
  // ever offers buckets that actually contain something.
  const repOptions = useMemo(() => {
    const counts = new Map();
    quotes.forEach((q) => {
      const name = repName(q);
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    const names = [...counts.keys()].sort((a, b) => {
      if (a === UNASSIGNED_REP) return 1;   // unattributed quotes sort last
      if (b === UNASSIGNED_REP) return -1;
      return a.localeCompare(b);
    });
    return names.map((name) => ({ id: name, label: name, count: counts.get(name) }));
  }, [quotes]);

  // Mirrors the backend: only the quote's creator or an admin may delete it.
  const canDeleteQuote = useCallback(
    (q) => isAdmin || Boolean(q?.created_by && q.created_by === currentUserId),
    [isAdmin, currentUserId]
  );

  const filtered = useMemo(() => {
    let out = quotes;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((x) =>
        (x.client_name || "").toLowerCase().includes(q) ||
        (x.client_entity || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") out = out.filter((x) => x.status === statusFilter);
    if (typeFilter !== "all-types") out = out.filter((x) => x.project_type === typeFilter);
    if (repFilter !== ALL_REPS) out = out.filter((x) => repName(x) === repFilter);
    const sorted = [...out];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case "oldest":    return (a.updated_at || "").localeCompare(b.updated_at || "");
        case "value-desc":return tcv(b) - tcv(a);
        case "value-asc": return tcv(a) - tcv(b);
        case "name-asc":  return (a.client_name || "").localeCompare(b.client_name || "");
        // Group each rep's quotes together, newest first inside the group.
        case "rep-asc":   return repName(a).localeCompare(repName(b)) ||
                                 (b.updated_at || "").localeCompare(a.updated_at || "");
        case "recent":
        default:          return (b.updated_at || "").localeCompare(a.updated_at || "");
      }
    });
    return sorted;
  }, [quotes, search, statusFilter, typeFilter, repFilter, sortKey]);

  // ── Bulk selection ──────────────────────────────────────────────────────
  // Only quotes this user is allowed to delete are ever selectable, so the
  // bulk bar can never promise a delete the backend will refuse.
  const deletableFiltered = useMemo(
    () => filtered.filter(canDeleteQuote),
    [filtered, canDeleteQuote]
  );
  const selectedCount = selectedIds.size;
  const allFilteredSelected =
    deletableFiltered.length > 0 && deletableFiltered.every((q) => selectedIds.has(q.id));

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      if (deletableFiltered.every((q) => prev.has(q.id))) {
        const next = new Set(prev);
        deletableFiltered.forEach((q) => next.delete(q.id));
        return next;
      }
      const next = new Set(prev);
      deletableFiltered.forEach((q) => next.add(q.id));
      return next;
    });

  const clearSelection = () => { setSelectedIds(new Set()); setBulkConfirm(false); };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/vexos-quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setSelectedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success("Quote deleted");
    } catch (e) {
      toast.error("Failed to delete quote");
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  // No bulk endpoint on purpose: firing the same authorized DELETE per quote
  // keeps one permission path, and reports partial failures honestly rather
  // than claiming a clean sweep.
  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => apiClient.delete(`/vexos-quotes/${id}`))
      );
      const deleted = ids.filter((_, i) => results[i].status === "fulfilled");
      const failed = ids.filter((_, i) => results[i].status === "rejected");
      if (deleted.length) {
        const gone = new Set(deleted);
        setQuotes((qs) => qs.filter((q) => !gone.has(q.id)));
      }
      setSelectedIds(new Set(failed));
      setBulkConfirm(false);
      if (failed.length) {
        results.forEach((r) => { if (r.status === "rejected") console.error(r.reason); });
        toast.error(
          deleted.length
            ? `Deleted ${deleted.length}, but ${failed.length} could not be deleted`
            : `Failed to delete ${failed.length} quote${failed.length === 1 ? "" : "s"}`
        );
      } else {
        toast.success(`Deleted ${deleted.length} quote${deleted.length === 1 ? "" : "s"}`);
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await apiClient.post(`/vexos-quotes/${id}/duplicate`);
      setQuotes((qs) => [res.data, ...qs]);
      toast.success("Quote duplicated");
    } catch (e) {
      toast.error("Failed to duplicate quote");
      console.error(e);
    }
  };

  const handleMarkSent = async (id) => {
    try {
      const res = await apiClient.patch(`/vexos-quotes/${id}`, { status: "proposal-sent" });
      setQuotes((qs) => qs.map((q) => (q.id === id ? res.data : q)));
      toast.success("Marked as Sent");
    } catch (e) {
      toast.error("Failed to update status");
      console.error(e);
    }
  };

  const handleRename = async (id) => {
    if (!renameText.trim()) { setRenameId(null); return; }
    try {
      const res = await apiClient.patch(`/vexos-quotes/${id}`, { client_name: renameText.trim() });
      setQuotes((qs) => qs.map((q) => (q.id === id ? res.data : q)));
      toast.success("Renamed");
    } catch (e) {
      toast.error("Failed to rename");
      console.error(e);
    } finally {
      setRenameId(null);
      setRenameText("");
    }
  };

  // `view === "full"` opens the long reference document instead of the
  // condensed client deck this button otherwise downloads.
  const openProposalPdf = async (id, view) => {
    try {
      const qs = view === "full" ? "?view=full" : "";
      const res = await apiClient.get(`/vexos-quotes/${id}/proposal-html${qs}`, {
        responseType: "text",
        transformResponse: [(d) => d],
      });
      const blob = new Blob([res.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      // Backend stamps proposal-sent; refresh in background.
      reload();
    } catch (e) {
      toast.error("Failed to open proposal PDF");
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] text-white" style={{ fontFamily: 'var(--font-sans)' }} data-testid="vexos-saved-quotes-page">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-12">
          <div>
            <p className="uppercase text-[#8FA79B] mb-3" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
              VEXOS Client Calculator
            </p>
            <h1 className="font-light text-[28px] sm:text-[48px]" style={{ fontFamily: 'var(--font-sans)', lineHeight: 1.05, letterSpacing: "-0.5px" }}>
              Saved Quotes
            </h1>
            <p className="text-white/45 mt-3" style={{ fontSize: "14px" }}>
              All active proposals and quotes across your pipeline
            </p>
            <Link
              to="/sales/calculators"
              className="inline-block mt-2 text-white/35 hover:text-white/60 transition-colors underline underline-offset-2"
              style={{ fontSize: "12px" }}
              data-testid="legacy-calculator-link"
            >
              Looking for the legacy calculator?
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto">
            {/* Strategy Call sits beside New VEXOS Quote but stays the
                secondary action — it sends paperwork, it does not price a
                project, so it never competes with the primary CTA. */}
            <button
              onClick={() => setInvoiceModal({ invoice: null })}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition-all w-full sm:w-auto"
              style={{ fontSize: "13px", fontWeight: 400 }}
              data-testid="custom-invoice-btn"
            >
              <Receipt className="w-4 h-4" />
              Custom Invoice
            </button>
            <button
              onClick={() => setStrategyCallOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition-all w-full sm:w-auto"
              style={{ fontSize: "13px", fontWeight: 400 }}
              data-testid="strategy-call-btn"
            >
              <FileSignature className="w-4 h-4" />
              Strategy Call
            </button>
            <button
              onClick={() => navigate("/sales/client-calculator/new")}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full transition-all w-full sm:w-auto"
              style={{ background: "#8FA79B", color: "#000000", fontSize: "13px", fontWeight: 400 }}
              data-testid="new-vexos-quote-btn"
            >
              <Plus className="w-4 h-4" />
              New VEXOS Quote
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <StatCard label="Total Quotes" value={stats.totalQuotes.toLocaleString()} sub="All statuses" testId="stat-total" />
          <StatCard label="Pipeline Value" value={fmtUSD(stats.pipelineValue)} sub="Across active quotes" testId="stat-pipeline" />
          <StatCard label="Proposals Sent" value={stats.proposalsSent.toLocaleString()} sub="Status: Sent" testId="stat-sent" />
        </div>

        {/* Search + status filters + type filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client or project…"
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-9 pr-4 py-2.5 text-[13px] text-white placeholder:text-white/35 focus:outline-none focus:border-white/30"
              data-testid="quotes-search-input"
            />
          </div>
          <FilterPills options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} testIdPrefix="status-filter" />
          <FilterPills options={TYPE_FILTERS}   value={typeFilter}   onChange={setTypeFilter}   testIdPrefix="type-filter" />
        </div>

        {/* Select-all + sales-rep filter + sort */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-4">
            {view === "list" && deletableFiltered.length > 0 && (
              <label
                className="inline-flex items-center gap-2 text-[12px] text-white/55 hover:text-white/85 transition-colors cursor-pointer select-none"
                data-testid="select-all-label"
              >
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  className="w-3.5 h-3.5 accent-[#8FA79B] cursor-pointer"
                  data-testid="select-all-quotes"
                />
                Select all
              </label>
            )}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setRepOpen((v) => !v); }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[11.5px] transition-all"
                style={repFilter === ALL_REPS
                  ? { borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.025)", color: "rgba(255,255,255,0.55)" }
                  : { borderColor: "rgba(143,167,155,0.45)", background: "rgba(143,167,155,0.18)", color: "#8FA79B" }}
                data-testid="rep-filter-btn"
              >
                Sales rep: <span className={repFilter === ALL_REPS ? "text-white/85" : ""}>{repFilter === ALL_REPS ? "All reps" : repFilter}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {repOpen && (
                <div
                  className="absolute left-0 top-full mt-2 bg-[#000000] border border-white/10 rounded-lg shadow-2xl py-1 z-20 min-w-[200px] max-h-[280px] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="rep-filter-menu"
                >
                  <button
                    onClick={() => { setRepFilter(ALL_REPS); setRepOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-[12px] hover:bg-white/[0.05] transition-colors ${repFilter === ALL_REPS ? "text-[#8FA79B]" : "text-white/75"}`}
                    data-testid="rep-filter-option-all"
                  >
                    All reps
                  </button>
                  {repOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => { setRepFilter(opt.id); setRepOpen(false); }}
                      className={`flex w-full items-center justify-between gap-4 px-4 py-2 text-[12px] hover:bg-white/[0.05] transition-colors ${repFilter === opt.id ? "text-[#8FA79B]" : "text-white/75"}`}
                      data-testid={`rep-filter-option-${opt.id}`}
                    >
                      <span className="truncate">{opt.label}</span>
                      <span className="text-white/35">{opt.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
          <FilterPills
            options={[
              { id: "list", label: <span className="inline-flex items-center gap-1.5"><ListIcon className="w-3.5 h-3.5" />List</span> },
              { id: "board", label: <span className="inline-flex items-center gap-1.5"><Columns3 className="w-3.5 h-3.5" />Board</span> },
            ]}
            value={view}
            onChange={changeView}
            testIdPrefix="view-toggle"
          />
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setSortOpen((v) => !v); }}
              className="inline-flex items-center gap-1 text-[12px] text-white/55 hover:text-white/85 transition-colors"
              data-testid="sort-dropdown-btn"
            >
              Sort by: <span className="text-white/85">{SORT_OPTIONS.find((s) => s.id === sortKey)?.label}</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {sortOpen && (
              <div
                className="absolute right-0 top-full mt-2 bg-[#000000] border border-white/10 rounded-lg shadow-2xl py-1 z-20"
                onClick={(e) => e.stopPropagation()}
                data-testid="sort-dropdown-menu"
              >
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => { setSortKey(opt.id); setSortOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-[12px] hover:bg-white/[0.05] transition-colors ${sortKey === opt.id ? "text-[#8FA79B]" : "text-white/75"}`}
                    data-testid={`sort-option-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Bulk actions — only appears once something is selected */}
        {view === "list" && selectedCount > 0 && (
          <div
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3"
            data-testid="bulk-actions-bar"
          >
            <p className="text-white/75 text-[12.5px]" data-testid="bulk-selected-count">
              {selectedCount} quote{selectedCount === 1 ? "" : "s"} selected
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 rounded-full text-[11.5px] text-white/60 hover:text-white transition-colors"
                data-testid="bulk-clear-btn"
              >
                Clear
              </button>
              <button
                onClick={() => setBulkConfirm(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11.5px] transition-all hover:brightness-115"
                style={{ background: "rgba(212,106,76,0.14)", color: "#D46A4C", border: "1px solid rgba(212,106,76,0.4)" }}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete selected
              </button>
            </div>
          </div>
        )}
        {view === "list" && selectedCount > 0 && bulkConfirm && (
          <div
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3"
            data-testid="bulk-delete-confirm"
          >
            <p className="text-red-300 text-[12px] flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" />
              Delete {selectedCount} quote{selectedCount === 1 ? "" : "s"}? This cannot be undone.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setBulkConfirm(false)}
                disabled={bulkBusy}
                className="px-3 py-1 rounded text-[11px] text-white/65 hover:text-white disabled:opacity-50"
                data-testid="bulk-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkBusy}
                className="px-3 py-1 rounded text-[11px] bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-60"
                data-testid="bulk-delete-confirm-yes"
              >
                {bulkBusy ? "Deleting…" : `Yes, delete ${selectedCount}`}
              </button>
            </div>
          </div>
        )}

        {/* Quote list */}
        {loading ? (
          <div className="text-center py-20 text-white/40 text-[13px]" data-testid="quotes-loading">Loading saved quotes…</div>
        ) : filtered.length === 0 ? (
          quotes.length === 0 ? (
            <EmptyState onCreate={() => navigate("/sales/client-calculator/new")} />
          ) : (
            <div className="text-center py-20 text-white/40 text-[13px]" data-testid="quotes-empty-filtered">
              No quotes match the current filters.
            </div>
          )
        ) : view === "board" ? (
          <QuoteBoard
            quotes={filtered}
            titles={boardTitles}
            onRenameColumn={renameColumn}
            onMove={moveQuote}
            onOpen={(q) => navigate(`/sales/client-calculator/${q.id}`)}
          />
        ) : (
          <div className="space-y-3" data-testid="quotes-list">
            {filtered.map((q) => (
              <QuoteCard
                key={q.id}
                quote={q}
                canDelete={canDeleteQuote(q)}
                selected={selectedIds.has(q.id)}
                onToggleSelect={() => toggleSelect(q.id)}
                isMenuOpen={menuOpenId === q.id}
                onOpenMenu={() => setMenuOpenId((v) => (v === q.id ? null : q.id))}
                isRenaming={renameId === q.id}
                renameText={renameText}
                onRenameTextChange={setRenameText}
                onStartRename={() => { setMenuOpenId(null); setRenameId(q.id); setRenameText(q.client_name || ""); }}
                onCommitRename={() => handleRename(q.id)}
                onCancelRename={() => { setRenameId(null); setRenameText(""); }}
                onEdit={() => (isInvoice(q) ? setInvoiceModal({ invoice: q }) : navigate(`/sales/client-calculator/${q.id}`))}
                onCopyLink={() => copyText(q.stripe_payment_link)}
                onViewSummary={() => navigate(`/sales/client-calculator/${q.id}?view=summary`)}
                onDownloadPdf={() => openProposalPdf(q.id)}
                onDownloadFullPdf={() => openProposalPdf(q.id, "full")}
                onDuplicate={() => { setMenuOpenId(null); handleDuplicate(q.id); }}
                onMarkSent={() => { setMenuOpenId(null); handleMarkSent(q.id); }}
                isConfirmingDelete={deletingId === q.id}
                onAskDelete={() => { setMenuOpenId(null); setDeletingId(q.id); }}
                onConfirmDelete={() => handleDelete(q.id)}
                onCancelDelete={() => setDeletingId(null)}
              />
            ))}
          </div>
        )}
      </div>

      <StrategyCallModal
        open={strategyCallOpen}
        onClose={() => setStrategyCallOpen(false)}
      />
      <CustomInvoiceModal
        open={Boolean(invoiceModal)}
        invoice={invoiceModal?.invoice || null}
        onClose={() => setInvoiceModal(null)}
        onSaved={upsertQuote}
      />
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, testId }) {
  return (
    <div
      className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-6"
      data-testid={testId}
    >
      <p className="uppercase text-[#8FA79B]" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>{label}</p>
      <p className="mt-3 text-white" style={{ fontFamily: 'var(--font-sans)', fontSize: "32px", lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </p>
      <p className="text-white/40 mt-2" style={{ fontSize: "11px" }}>{sub}</p>
    </div>
  );
}

function FilterPills({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="inline-flex items-center gap-1 bg-white/[0.025] border border-white/10 rounded-full p-1">
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            className="px-3.5 py-1.5 rounded-full text-[11.5px] transition-all"
            style={{
              background: active ? "rgba(143,167,155,0.18)" : "transparent",
              color: active ? "#8FA79B" : "rgba(255,255,255,0.55)",
              fontWeight: active ? 500 : 400,
            }}
            data-testid={`${testIdPrefix}-${opt.id}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Board view ────────────────────────────────────────────────────────────
// Proposals only: an invoice bills work already sold, so it is not a lead and
// has no temperature. Within a column, cards keep the page's chosen sort.
function QuoteBoard({ quotes, titles, onRenameColumn, onMove, onOpen }) {
  const leads = quotes.filter((q) => !isInvoice(q));
  const byColumn = Object.fromEntries(BOARD_COLUMNS.map((c) => [c.id, []]));
  leads.forEach((q) => byColumn[temperatureOf(q)].push(q));

  const handleDragEnd = ({ draggableId, destination }) => {
    if (!destination) return;
    onMove(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start" data-testid="quotes-board">
        {BOARD_COLUMNS.map((col, idx) => {
          const items = byColumn[col.id];
          const total = items.reduce((s, q) => s + tcv(q), 0);
          return (
            <div
              key={col.id}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] min-w-0"
              data-testid={`board-column-${col.id}`}
            >
              <div className="px-4 pt-4 pb-3 border-b border-white/[0.06]" style={{ borderTop: `2px solid ${col.accent}`, borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
                <div className="flex items-center justify-between gap-2">
                  <EditableColumnTitle
                    value={titles[col.id] || DEFAULT_BOARD_TITLES[col.id]}
                    accent={col.accent}
                    onSave={(t) => onRenameColumn(col.id, t)}
                    testId={`board-column-title-${col.id}`}
                  />
                  <span className="text-white/40 text-[11px] flex-shrink-0" data-testid={`board-column-count-${col.id}`}>
                    {items.length}
                  </span>
                </div>
                <p className="text-white/45 mt-1 text-[12px]" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {fmtUSD(total)}
                </p>
              </div>
              <Droppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="p-3 space-y-2.5 min-h-[120px] transition-colors rounded-b-xl"
                    style={{ background: snapshot.isDraggingOver ? "rgba(255,255,255,0.035)" : "transparent" }}
                  >
                    {items.map((q, index) => (
                      <Draggable key={q.id} draggableId={q.id} index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <BoardCard
                            quote={q}
                            provided={dragProvided}
                            dragging={dragSnapshot.isDragging}
                            prevColumn={BOARD_COLUMNS[idx - 1]}
                            nextColumn={BOARD_COLUMNS[idx + 1]}
                            titles={titles}
                            onMove={(to) => onMove(q.id, to)}
                            onOpen={() => onOpen(q)}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {items.length === 0 && !snapshot.isDraggingOver && (
                      <p className="text-center text-white/25 text-[12px] py-8">Drag quotes here</p>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}

function EditableColumnTitle({ value, accent, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  useEffect(() => { if (!editing) setText(value); }, [value, editing]);

  const commit = () => { setEditing(false); onSave(text); };

  if (editing) {
    return (
      <input
        autoFocus
        value={text}
        maxLength={40}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setEditing(false); setText(value); }
        }}
        onBlur={commit}
        className="min-w-0 flex-1 bg-white/[0.06] border border-white/15 rounded px-2 py-0.5 text-[13px] text-white uppercase focus:outline-none focus:border-[#8FA79B]"
        style={{ letterSpacing: "0.14em" }}
        data-testid={`${testId}-input`}
      />
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="group/title inline-flex items-center gap-1.5 min-w-0 text-left"
      title="Rename column"
      data-testid={testId}
    >
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent }} />
      <span className="truncate uppercase text-white text-[12px]" style={{ letterSpacing: "0.16em" }}>{value}</span>
      <Pencil className="w-3 h-3 text-white/30 flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover/title:opacity-100 transition-opacity" />
    </button>
  );
}

/** "Condo" · "Community" · "Master Plan" (a community quote with the flag) · "sfh". */
function projectTypeLabel(quote) {
  if (quote.project_type === "condo") return "Condo";
  if (quote.project_type === "community") return quote.master_plan ? "Master Plan" : "Community";
  return quote.project_type || "—";
}

function BoardCard({ quote, provided, dragging, prevColumn, nextColumn, titles, onMove, onOpen }) {
  const value = tcv(quote);
  const projectType = projectTypeLabel(quote);
  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onClick={(e) => { if (!e.target.closest("button")) onOpen(); }}
      className="group rounded-lg border px-3.5 py-3 cursor-pointer transition-colors"
      style={{
        ...provided.draggableProps.style,
        background: dragging ? "#141414" : "rgba(255,255,255,0.03)",
        borderColor: dragging ? "rgba(143,167,155,0.5)" : "rgba(255,255,255,0.07)",
        opacity: quote.status === "draft" ? 0.75 : 1,
      }}
      data-testid={`board-card-${quote.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-white text-[13.5px] min-w-0 truncate">{quote.client_name || "Untitled"}</p>
        <StatusPill status={quote.status || "saved"} />
      </div>
      <p className="text-white mt-1.5 text-[15px]" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value > 0 ? fmtUSD(value) : "—"}
      </p>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <p className="text-white/45 text-[11px] min-w-0 truncate">
          {projectType} · <span className="text-[#8FA79B]/80">{repName(quote)}</span>
        </p>
        {/* Click-to-move for touch screens, where dragging fights scrolling. */}
        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {prevColumn && (
            <button
              onClick={() => onMove(prevColumn.id)}
              className="p-1 rounded text-white/45 hover:text-white hover:bg-white/[0.06]"
              aria-label={`Move to ${titles[prevColumn.id]}`}
              title={`Move to ${titles[prevColumn.id]}`}
              data-testid={`board-move-${prevColumn.id}-${quote.id}`}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          )}
          {nextColumn && (
            <button
              onClick={() => onMove(nextColumn.id)}
              className="p-1 rounded text-white/45 hover:text-white hover:bg-white/[0.06]"
              aria-label={`Move to ${titles[nextColumn.id]}`}
              title={`Move to ${titles[nextColumn.id]}`}
              data-testid={`board-move-${nextColumn.id}-${quote.id}`}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      className="text-center py-20 border border-dashed border-white/15 rounded-2xl"
      data-testid="quotes-empty-state"
    >
      <p className="text-white/85" style={{ fontFamily: 'var(--font-sans)', fontSize: "24px" }}>
        No quotes saved yet.
      </p>
      <p className="text-white/45 mt-2" style={{ fontSize: "13px" }}>
        Create your first VEXOS quote to get started.
      </p>
      <button
        onClick={onCreate}
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full"
        style={{ background: "#8FA79B", color: "#000000", fontSize: "12px", fontWeight: 400 }}
        data-testid="empty-state-new-quote-btn"
      >
        <Plus className="w-4 h-4" />
        New VEXOS Quote
      </button>
    </div>
  );
}

function QuoteCard({
  quote, canDelete = true, selected = false, onToggleSelect, isMenuOpen, onOpenMenu,
  isRenaming, renameText, onRenameTextChange, onStartRename, onCommitRename, onCancelRename,
  onEdit, onViewSummary, onDownloadPdf, onDownloadFullPdf, onDuplicate, onMarkSent, onCopyLink,
  isConfirmingDelete, onAskDelete, onConfirmDelete, onCancelDelete,
}) {
  const value = tcv(quote);
  const invoice = isInvoice(quote);
  const isDraft = quote.status === "draft";
  const bundle = bundleLabel(quote);
  const projectType = projectTypeLabel(quote);
  const inputs = quote.inputs || {};
  const count =
    quote.project_type === "condo"
      ? `${Number(inputs.units || 0)} units`
      : `${Number(inputs.c_lots || 0)} ${quote.master_plan ? "units" : "lots"}`;
  const term = `${Number(quote.contract_term || 12)}-mo`;
  const rep = repName(quote);

  return (
    <div
      className={`relative group rounded-xl px-6 py-5 transition-all hover:bg-white/[0.04] hover:border-white/15 ${
        selected
          ? "bg-[#8FA79B]/[0.08] border border-[#8FA79B]/40"
          : "bg-white/[0.025] border border-white/[0.06]"
      }`}
      style={{ opacity: isDraft ? 0.72 : 1 }}
      onClick={(e) => {
        // Card click = Edit, but only on the body (not buttons or rename input).
        if (e.target.closest("button, input, a, [data-no-card-click]")) return;
        if (isConfirmingDelete || isRenaming) return;
        onEdit();
      }}
      data-testid={`quote-card-${quote.id}`}
    >
      <div className="flex items-start gap-3 sm:gap-6">
        {canDelete && (
          <div className="pt-1 flex-shrink-0" data-no-card-click>
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 accent-[#8FA79B] cursor-pointer"
              aria-label={`Select ${quote.client_name || "quote"}`}
              data-testid={`select-quote-${quote.id}`}
            />
          </div>
        )}
        {/* Left — name + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isRenaming ? (
              <input
                autoFocus
                value={renameText}
                onChange={(e) => onRenameTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onCommitRename();
                  if (e.key === "Escape") onCancelRename();
                }}
                onBlur={onCommitRename}
                className="bg-white/[0.06] border border-white/15 rounded px-2 py-1 text-[15px] text-white focus:outline-none focus:border-[#8FA79B]"
                style={{ minWidth: "260px" }}
                data-no-card-click
                data-testid={`rename-input-${quote.id}`}
              />
            ) : (
              <p className="text-white" style={{ fontSize: "15px", fontWeight: 400 }} data-testid={`quote-name-${quote.id}`}>
                {quote.client_name || "Untitled"}
              </p>
            )}
            {isDraft && (
              <span className="text-white/40" style={{ fontSize: "12px" }}>— Draft</span>
            )}
            {invoice ? (
              <>
                <span
                  className="inline-flex items-center gap-1"
                  style={{
                    fontSize: "10px", letterSpacing: "0.16em", textTransform: "uppercase",
                    padding: "3px 9px", borderRadius: "999px", whiteSpace: "nowrap",
                    background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.7)",
                    border: "1px solid rgba(255,255,255,0.14)",
                  }}
                  data-testid={`invoice-badge-${quote.id}`}
                >
                  <Receipt className="w-3 h-3" /> Invoice
                </span>
                <InvoiceStatusPill quote={quote} />
              </>
            ) : (
              <StatusPill status={quote.status || "saved"} />
            )}
            {bundle && (
              <span
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  padding: "3px 9px",
                  borderRadius: "999px",
                  background: "rgba(143,167,155,0.16)",
                  color: "#8FA79B",
                  border: "1px solid rgba(143,167,155,0.4)",
                }}
                data-testid={`bundle-badge-${quote.id}`}
              >
                {bundle}
              </span>
            )}
          </div>
          <p className="text-white/45 mt-2" style={{ fontSize: "11.5px" }}>
            {invoice
              ? (() => {
                  const n = (quote.invoice?.line_items || []).length;
                  return `${[quote.client_entity, `${n} line${n === 1 ? "" : "s"}`].filter(Boolean).join(" · ")} · Created ${fmtDate(quote.created_at)}`;
                })()
              : `${projectType} · ${count} · ${term} · Saved ${fmtDate(quote.updated_at)}`}
            <span className="text-[#8FA79B]/80" data-testid={`quote-rep-${quote.id}`}> · {rep}</span>
          </p>
        </div>

        {/* Right — TCV + actions */}
        <div className="flex-shrink-0 text-right pr-7 sm:pr-0">
          <p
            className="text-white"
            style={{ fontFamily: 'var(--font-sans)', fontSize: "20px", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}
            data-testid={`quote-tcv-${quote.id}`}
          >
            {value > 0 ? fmtUSD(value) : "—"}
          </p>
          <p className="text-white/35 mt-1" style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            {invoice ? "Invoice Total" : "Total Contract Value"}
          </p>
        </div>

        {/* Overflow menu (top-right of card, hover-revealed) */}
        <div className="absolute top-4 right-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity" data-no-card-click>
          <button
            onClick={(e) => { e.stopPropagation(); onOpenMenu(); }}
            className="p-1.5 rounded text-white/55 hover:text-white hover:bg-white/[0.05]"
            data-testid={`quote-menu-btn-${quote.id}`}
            aria-label="More actions"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {isMenuOpen && (
            <div
              className="absolute right-0 top-full mt-1 bg-[#000000] border border-white/10 rounded-lg shadow-2xl py-1 z-20 min-w-[180px]"
              onClick={(e) => e.stopPropagation()}
              data-testid={`quote-menu-${quote.id}`}
            >
              {!invoice && (
                <MenuItem icon={Copy} label="Duplicate" onClick={onDuplicate} testId={`menu-duplicate-${quote.id}`} />
              )}
              {!invoice && quote.status !== "proposal-sent" && (
                <MenuItem icon={CheckCircle2} label="Mark as Sent" onClick={onMarkSent} testId={`menu-mark-sent-${quote.id}`} />
              )}
              <MenuItem icon={Edit3} label="Rename" onClick={onStartRename} testId={`menu-rename-${quote.id}`} />
              {canDelete && (
                <MenuItem icon={Trash2} label="Delete" danger onClick={onAskDelete} testId={`menu-delete-${quote.id}`} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action button row */}
      <div className="mt-4 pt-3 border-t border-white/[0.05] flex flex-wrap items-center gap-2" data-no-card-click>
        {invoice ? (
          <>
            <ActionBtn onClick={onEdit} testId={`view-invoice-${quote.id}`}>
              <Eye className="w-3 h-3" /> {quote.stripe_payment_status === "pending" ? "View · Check payment" : "View Invoice"}
            </ActionBtn>
            {quote.stripe_payment_status === "pending" && quote.stripe_payment_link && (
              <>
                <ActionBtn gold onClick={onCopyLink} testId={`copy-invoice-link-${quote.id}`}>
                  <CopyIcon className="w-3 h-3" /> Copy Payment Link
                </ActionBtn>
                <ActionBtn onClick={() => window.open(quote.stripe_payment_link, "_blank", "noopener,noreferrer")} testId={`open-invoice-link-${quote.id}`}>
                  <CreditCard className="w-3 h-3" /> Open Link
                </ActionBtn>
              </>
            )}
          </>
        ) : isDraft ? (
          <>
            <ActionBtn onClick={onEdit} testId={`continue-${quote.id}`}>Continue</ActionBtn>
            {canDelete && (
              <ActionBtn onClick={onAskDelete} danger testId={`delete-${quote.id}`}>Delete</ActionBtn>
            )}
          </>
        ) : (
          <>
            <ActionBtn onClick={onEdit} testId={`edit-${quote.id}`}>
              <Edit3 className="w-3 h-3" /> Edit
            </ActionBtn>
            <ActionBtn onClick={onViewSummary} testId={`view-summary-${quote.id}`}>
              <Eye className="w-3 h-3" /> View Summary
            </ActionBtn>
            <ActionBtn gold onClick={onDownloadPdf} testId={`download-pdf-${quote.id}`}>
              <FileText className="w-3 h-3" /> Download PDF
            </ActionBtn>
            <ActionBtn onClick={onDownloadFullPdf} testId={`download-full-pdf-${quote.id}`}>
              <FileText className="w-3 h-3" /> Full Version
            </ActionBtn>
          </>
        )}
      </div>

      {/* Inline delete confirmation */}
      {isConfirmingDelete && (
        <div
          className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-between gap-3"
          data-no-card-click
          data-testid={`delete-confirm-${quote.id}`}
        >
          <p className="text-red-300 text-[12px] flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            Delete this quote? This cannot be undone.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancelDelete}
              className="px-3 py-1 rounded text-[11px] text-white/65 hover:text-white"
              data-testid={`delete-cancel-${quote.id}`}
            >
              Cancel
            </button>
            <button
              onClick={onConfirmDelete}
              className="px-3 py-1 rounded text-[11px] bg-red-500/80 hover:bg-red-500 text-white"
              data-testid={`delete-confirm-yes-${quote.id}`}
            >
              Yes, delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-[12px] transition-colors"
      style={{
        color: danger ? "#D46A4C" : "rgba(255,255,255,0.85)",
      }}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function ActionBtn({ children, onClick, gold = false, danger = false, testId }) {
  let style;
  if (gold) {
    style = { background: "rgba(143,167,155,0.12)", color: "#8FA79B", border: "1px solid rgba(143,167,155,0.4)" };
  } else if (danger) {
    style = { background: "transparent", color: "#D46A4C", border: "1px solid transparent" };
  } else {
    style = { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.78)", border: "1px solid rgba(255,255,255,0.08)" };
  }
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] transition-all hover:brightness-115"
      style={style}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
